import { asciiSafeStringify } from '@@/bits/json_stringify'
import { desktopShellManifest, MAX_STORE_RECEIPT_CHARS } from '@padlet/desktop-shell-protocol'
import { deserializeMessageType, NativeBridge, type RawMessageContent } from '@padlet/native-bridge-core'

type DesktopShellBridge = NativeBridge<typeof desktopShellManifest, 'web'>

const ELECTRON_BRIDGE_ACCESSOR = 'padlet'

// Cap inbound native messages at 64 KiB. Match the native DesktopBridgeHost limit.
const MAX_INBOUND_MESSAGE_BYTES = 64 * 1024

interface DesktopShellPreloadApi {
  postDesktopBridgeMessage(message: Record<string, unknown>): void
  subscribeToDesktopBridgeMessages(listener: (message: Record<string, unknown>) => void): () => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(record).sort()
  const expected = [...keys].sort()
  return actual.length === expected.length && actual.every((key, index) => key === expected[index])
}

// Log the drop reason only. Do not log the message body.
function warnDropped(reason: string): void {
  console.warn(`[desktop_shell/transport] Dropped an inbound message: ${reason}`)
}

function schemaValidationDropReason(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'issues' in error &&
    Array.isArray((error as { issues: unknown }).issues)
  ) {
    const issues = (error as { issues: Array<{ code?: unknown; path?: unknown }> }).issues
    const parts = issues.map((issue) => {
      const code = typeof issue.code === 'string' ? issue.code : 'unknown'
      const path = Array.isArray(issue.path) && issue.path.length > 0 ? issue.path.join('.') : '(root)'
      return `${code}:${path}`
    })
    return parts.length > 0 ? parts.join(',') : 'zod'
  }
  return 'invalid payload'
}

function inboundSizeLimit(value: unknown): number {
  if (isRecord(value) && value.messageType === 'settleStorePurchase') {
    return MAX_STORE_RECEIPT_CHARS + MAX_INBOUND_MESSAGE_BYTES
  }
  return MAX_INBOUND_MESSAGE_BYTES
}

function exceedsMaxInboundSize(value: unknown): boolean {
  try {
    return new TextEncoder().encode(asciiSafeStringify(value)).length > inboundSizeLimit(value)
  } catch {
    return true
  }
}

function getPreloadApi(): DesktopShellPreloadApi | undefined {
  const candidate = (window as unknown as Record<string, unknown>)[ELECTRON_BRIDGE_ACCESSOR]
  if (
    isRecord(candidate) &&
    typeof candidate.postDesktopBridgeMessage === 'function' &&
    typeof candidate.subscribeToDesktopBridgeMessages === 'function'
  ) {
    return candidate as unknown as DesktopShellPreloadApi
  }
  return undefined
}

type NativeToWebMessageType = keyof typeof desktopShellManifest.nativeToWeb
type WebToNativeMessageType = keyof typeof desktopShellManifest.webToNative

const nativeToWebMessageTypes = new Set<string>(Object.keys(desktopShellManifest.nativeToWeb))
const webToNativeMessageTypes = new Set<string>(Object.keys(desktopShellManifest.webToNative))

function isNativeToWebMessageType(value: string): value is NativeToWebMessageType {
  return nativeToWebMessageTypes.has(value)
}
function isWebToNativeMessageType(value: string): value is WebToNativeMessageType {
  return webToNativeMessageTypes.has(value)
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0
}

function isRepliableConstructor(constructor: unknown): boolean {
  return isRecord(constructor) && typeof (constructor as Record<string, unknown>).reply === 'function'
}

function callPayloadConstructor(constructor: unknown, payload: unknown): unknown {
  if (typeof constructor === 'function') return (constructor as (value: unknown) => unknown)(payload)
  if (isRecord(constructor) && typeof constructor.payload === 'function') {
    return (constructor.payload as (value: unknown) => unknown)(payload)
  }
  throw new TypeError('Desktop shell manifest constructor has no payload validator')
}

function parseErrorReplyPayload(payload: unknown): { message: string } {
  if (!isRecord(payload) || !hasExactKeys(payload, ['message']) || typeof payload.message !== 'string') {
    throw new TypeError('Expected a desktop bridge error payload')
  }
  return { message: payload.message }
}

// NativeBridge encodes undefined as a sentinel object. Decode the sentinel before schema checks. Do not accept the sentinel as a real payload.
function decodeNativeBridgeMessageContent(value: unknown): unknown {
  if (isRecord(value) && value.__nativeBridgeValueType === 'undefined') return undefined
  if (typeof value !== 'object' || value === null) return value
  if (Array.isArray(value)) return value.map((element) => decodeNativeBridgeMessageContent(element))
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, decodeNativeBridgeMessageContent(entry)]))
}

// Native payloads are untrusted. Validate each payload before NativeBridge.receiveMessage. Drop a message that fails.
function parseTrustedNativeMessage(value: unknown): RawMessageContent | { internalMessageType: string } | undefined {
  if (exceedsMaxInboundSize(value)) {
    warnDropped('exceeds the inbound size limit')
    return undefined
  }
  if (!isRecord(value)) return undefined

  if ('internalMessageType' in value) {
    // Native to web handshake allows nativeBridgeReady only.
    if (hasExactKeys(value, ['internalMessageType']) && value.internalMessageType === 'nativeBridgeReady') {
      return { internalMessageType: value.internalMessageType }
    }
    warnDropped('unsupported internal message type')
    return undefined
  }

  if (typeof value.messageType !== 'string') return undefined
  const { originalMessageType: messageType, classification } = deserializeMessageType(value.messageType)

  try {
    if (classification === 'normal') {
      if (!isNativeToWebMessageType(messageType)) {
        warnDropped(`unknown native -> web message type "${messageType}"`)
        return undefined
      }
      const constructor = desktopShellManifest.nativeToWeb[messageType]
      const repliable = isRepliableConstructor(constructor)

      if (repliable) {
        if (!hasExactKeys(value, ['messageId', 'messageType', 'payload']) || !isPositiveSafeInteger(value.messageId)) {
          warnDropped(`repliable message "${messageType}" missing a valid messageId`)
          return undefined
        }
      } else if (!hasExactKeys(value, ['messageType', 'payload'])) {
        warnDropped(`payload-only message "${messageType}" carries an unexpected field`)
        return undefined
      }

      const payload = callPayloadConstructor(constructor, decodeNativeBridgeMessageContent(value.payload))
      return repliable ? { messageType, messageId: value.messageId as number, payload } : { messageType, payload }
    }

    // Validate a result or error reply against webToNative. The original message is a web to native call.
    if (!isWebToNativeMessageType(messageType)) {
      warnDropped(`unknown web -> native message type "${messageType}" in a ${classification} reply`)
      return undefined
    }
    if (!hasExactKeys(value, ['messageId', 'messageType', 'payload']) || !isPositiveSafeInteger(value.messageId)) {
      warnDropped(`${classification} reply for "${messageType}" missing a valid messageId`)
      return undefined
    }

    if (classification === 'error') {
      return {
        messageType: value.messageType,
        messageId: value.messageId,
        payload: parseErrorReplyPayload(value.payload),
      }
    }

    const constructor = desktopShellManifest.webToNative[messageType]
    const decodedPayload = decodeNativeBridgeMessageContent(value.payload)
    const payload = isRecord(constructor)
      ? (constructor.reply as (value: unknown) => unknown)(decodedPayload)
      : undefined
    return { messageType: value.messageType, messageId: value.messageId, payload }
  } catch (error) {
    warnDropped(`schema validation failed for "${messageType}": ${schemaValidationDropReason(error)}`)
    return undefined
  }
}

let sharedBridge: DesktopShellBridge | undefined
let unsubscribeFromPreload: (() => void) | undefined
let removeMessageSender: (() => void) | undefined

function createDesktopShellTransport(): DesktopShellBridge {
  const bridge = new NativeBridge(desktopShellManifest, 'web')

  const senderSubscription = bridge.addMessageSender((messageContent) => {
    getPreloadApi()?.postDesktopBridgeMessage(messageContent as Record<string, unknown>)
  })
  removeMessageSender = () => senderSubscription.remove()

  const subscribeResult = getPreloadApi()?.subscribeToDesktopBridgeMessages((message) => {
    const parsed = parseTrustedNativeMessage(message)
    if (parsed) bridge.receiveMessage(parsed as RawMessageContent)
  })
  unsubscribeFromPreload = typeof subscribeResult === 'function' ? subscribeResult : undefined

  return bridge
}

function getDesktopShellTransport(): DesktopShellBridge {
  if (!sharedBridge) sharedBridge = createDesktopShellTransport()
  return sharedBridge
}

function teardownDesktopShellTransport(): void {
  const bridge = sharedBridge
  const unsubscribe = unsubscribeFromPreload
  const removeSender = removeMessageSender

  sharedBridge = undefined
  unsubscribeFromPreload = undefined
  removeMessageSender = undefined

  try {
    removeSender?.()
  } catch (error) {
    console.warn('[desktop_shell/transport] Failed to remove the outgoing message sender during teardown', error)
  }
  try {
    unsubscribe?.()
  } catch (error) {
    console.warn(
      '[desktop_shell/transport] Failed to unsubscribe from the preload message stream during teardown',
      error,
    )
  }
  try {
    bridge?.reset()
  } catch (error) {
    console.warn('[desktop_shell/transport] Failed to reset the bridge during teardown', error)
  }
}

export { getDesktopShellTransport, parseTrustedNativeMessage, teardownDesktopShellTransport, type DesktopShellBridge }
